import { Component, OnInit, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';

// material
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

// core imports
import { environment } from '@environments/environment';
import { AppEvent, AppEventType, EventQueueService } from '@core/events/events-queue.service';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { LoggedUserModel } from '@core/authentication/logged-user.model';
import { UserService } from '@core/services/user.service';

// shared imports
import { SideMenusService } from '@shared/components/side-menus/side-menus.service';
import { isAssociationLogin } from '@shared/utilities/association-role.util';

// feature imports
import { BasketStore } from '@features/associations/components/basket/basket.store';
import { AssociationsStore } from '@features/associations/store/associations.store';
import { HelpStore, helpArticleFor } from '@features/help';

@Component({
  selector: 'app-top-navbar',
  templateUrl: './top-navbar.component.html',
  styleUrls: ['./top-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatToolbarModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatMenuModule,
    MatChipsModule,
    MatTooltipModule,
  ],
})
export class TopNavbarComponent implements OnInit {
  // ============================================
  // Dependencies
  // ============================================
  private readonly sideMenusService = inject(SideMenusService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly eventQueue = inject(EventQueueService);

  private readonly helpStore = inject(HelpStore);

  readonly basketStore = inject(BasketStore);
  readonly associationsStore = inject(AssociationsStore);

  // ============================================
  // Router State
  // ============================================
  private readonly navigationEnd = toSignal(
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
    { initialValue: null },
  );

  // ============================================
  // User State (Signals)
  // ============================================
  readonly loggedUser = signal<LoggedUserModel | null>(null);
  /**
   * An organisation's person (16/17/19–22), who books from the association flow and can switch
   * between their organisations. Derived from the user, so everything reading it (help article,
   * basket, menu) flips together.
   */
  readonly isAssociationLogin = computed(() => isAssociationLogin(this.loggedUser()?.primaryRole));
  readonly canManageAssociations = signal<boolean>(false);

  // ============================================
  // UI State (Signals)
  // ============================================
  readonly isFocusModeEnabled = signal<boolean>(false);
  readonly showBasketIcon = signal<boolean>(false);

  // ============================================
  // Computed Values
  // ============================================
  /** The leaf route after the last navigation — where a page's `data` lives. */
  private readonly leafRoute = computed(() => {
    this.navigationEnd();
    let route = this.activatedRoute;
    while (route.firstChild) route = route.firstChild;
    return route;
  });

  readonly routeHasFocusMode = computed(() => this.leafRoute().snapshot.data['routeHasFocusMode'] ?? false);

  /**
   * The help article for this page (`helpRouteData(...)` on its route), or null for no "?" button.
   * A page both sides use names an article per side; administrators get the second one. The
   * button is only offered for an article this reader may open (see `HelpStore.openableKeys`), so
   * it never leads to "Vejledningen findes ikke" — the page names the article by its route, not by
   * who is allowed to read it.
   */
  readonly helpArticle = computed<string | null>(() => {
    const article = helpArticleFor(this.leafRoute().snapshot.data, this.isAssociationLogin());
    return article && this.helpStore.openableKeys().has(article) ? article : null;
  });

  readonly selectedAssociation = computed(() => this.associationsStore.selectedEntity());

  readonly userOrAssociationName = computed(() => {
    const selectedAssociation = this.selectedAssociation();
    return selectedAssociation && this.isAssociationLogin()
      ? selectedAssociation.name
      : (this.loggedUser()?.email ?? '');
  });

  // ============================================
  // Constants
  // ============================================
  readonly env = environment;

  // ============================================
  // Lifecycle
  // ============================================
  constructor() {
    effect(() => {
      this.navigationEnd();
      this.checkActiveRoute();
    });
  }

  async ngOnInit() {
    // Seeded with null until the session is settled — the same wait as the main menu.
    const user = await firstValueFrom(
      this.authenticationService.loggedUserSubject.pipe(filter((u) => u?.primaryRole !== undefined)),
    );
    this.loggedUser.set(user);

    // Which articles the reader may open, so the "?" is only shown for those. Cheap to ask: the
    // permission answers are cached per session, and Hjælp asks the same on every visit.
    void this.helpStore.loadAccess();

    this.canManageAssociations.set(await firstValueFrom(this.userService.canI('manage_associations')));

    this.checkActiveRoute();
  }

  // ============================================
  // Public Methods - UI Interactions
  // ============================================
  togglePageFocusMode() {
    this.isFocusModeEnabled.update((value) => !value);

    const sideMenuAction = this.isFocusModeEnabled() ? 'close' : 'open';
    this.sideMenusService.toggleMainMenuSubject.next(sideMenuAction);

    this.eventQueue.dispatch(
      new AppEvent(AppEventType.ClickedPageFocusMode, { isFocusModeEnabled: this.isFocusModeEnabled() }),
    );
  }

  toggleMainMenu() {
    this.sideMenusService.toggleMainMenuSubject.next('toggle');
  }

  toggleBasket() {
    this.sideMenusService.renderAltMenuSubject.next('basket');
    this.sideMenusService.toggleAltMenuSubject.next('toggle');
  }

  signIn() {
    this.authenticationService.signIn();
  }

  signOut() {
    // Permission answers are cached per session — see UserService.canI().
    this.userService.clearPermissionCache();
    this.authenticationService.signOut();
  }

  // ============================================
  // Private Helpers - Route Management
  // ============================================
  private checkActiveRoute() {
    const currentRoute = this.activatedRoute.root;
    const isInAssociations = this.isRouteActive(currentRoute, 'associations');
    const isOnEventRequest = this.isRouteActive(currentRoute, 'event-request');

    this.showBasketIcon.set(isInAssociations && !isOnEventRequest);
  }

  private isRouteActive(route: ActivatedRoute, path: string): boolean {
    if (route.routeConfig?.path === path) {
      return true;
    }
    return route.children?.some((child) => this.isRouteActive(child, path)) ?? false;
  }
}
